import React from "react";
import { PageLayout } from "@/components/layout/index";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { 
  ItemType, 
  ItemTypeConfig, 
  PageHandlers, 
  GroupedCards,
  WizardManager,
  PendingChangesProps,
  FlowViewProps,
} from "../types";

interface UnifiedItemsContentProps {
  itemType: ItemType;
  config: ItemTypeConfig;
  handlers: PageHandlers;
  
  // Data
  data: any[];
  cards: CardData[];
  groupedCards: GroupedCards[];
  selectedGame: any;
  tags: any[];
  selectedFolderId: string | null;
  
  // Page layout
  pageLayoutConfig: any;
  isEditMode: boolean;
  setIsEditMode: (editMode: boolean) => void;
  handlePageConfigChange: (config: any) => void;
  
  // Handlers
  handleCardClick: (card: CardData) => void;
  handleDelete: (id: string) => Promise<void>;
  handleAddWithFolder: () => void;
  handleDeleteSelected: (ids: string[]) => Promise<void>;
  refreshData: () => Promise<void>;
  
  // Data view mode props
  availableSections: any[];
  availableFields: any[];
  rawItems: any[];
  templateLookup: Record<string, any>;
  folderPathLookup: (folderId: string | null | undefined) => string;
  
  // Filter configs
  filterConfigs: any;
  
  // Wizard manager for edit handlers
  wizardManager: WizardManager;
  
  // Optional pending changes props
  pendingChangesProps?: PendingChangesProps | null;
  
  // Optional flow view props
  flowViewProps?: FlowViewProps | null;
  
  // Cell value change handler (optional, for table editing)
  handleCellValueChange?: (rowKey: string, cellKey: string, newValue: any) => void;
}

/**
 * Content component that renders the main PageLayout with all necessary props
 * Handles the configuration merging and prop passing to PageLayout
 */
export function UnifiedItemsContent({
  itemType,
  config,
  handlers,
  data,
  cards,
  groupedCards,
  selectedGame,
  tags,
  selectedFolderId,
  pageLayoutConfig,
  isEditMode,
  setIsEditMode,
  handlePageConfigChange,
  handleCardClick,
  handleDelete,
  handleAddWithFolder,
  handleDeleteSelected,
  refreshData,
  availableSections,
  availableFields,
  rawItems,
  templateLookup,
  folderPathLookup,
  filterConfigs,
  wizardManager,
  pendingChangesProps,
  flowViewProps,
  handleCellValueChange,
}: UnifiedItemsContentProps) {
  const {
    setSelectedBlueprint,
    setIsEditModalOpen,
    setSelectedGlossaryItem,
    setIsBulkCreateWizardOpen,
  } = wizardManager;

  // Custom card renderer that adds handlers to each card
  const renderCard = (card: CardData) => {
    // Build edit handler based on item type
    let onEdit: ((id: string) => void) | undefined;
    
    if (handlers.onEdit) {
      // Use page-provided edit handler
      onEdit = (id: string) => {
        const navigate = (path: string) => {
          window.location.href = path;
        };
        handlers.onEdit?.(id, data, navigate);
      };
    } else {
      // Fallback to wizard-based edit
      if (itemType === "blueprints") {
        onEdit = (id: string) => {
          const blueprint = data.find((bp: any) => bp.id === id);
          if (blueprint) {
            setSelectedBlueprint(blueprint);
            setIsEditModalOpen(true);
          }
        };
      } else if (itemType === "library") {
        onEdit = (id: string) => {
          const glossaryItem = data.find((item: any) => item.id === id);
          if (glossaryItem) {
            setSelectedGlossaryItem(glossaryItem);
            setIsEditModalOpen(true);
          }
        };
      } else if (itemType === "dialogs") {
        onEdit = (id: string) => {
          // Navigate to dialog detail page
          window.location.href = `/dialogs/${id}`;
        };
      }
    }

    // Add handlers to the card
    const cardWithHandlers: CardData = {
      ...card,
      onDelete: handleDelete,
      onEdit,
    };

    return handlers.renderCard(cardWithHandlers, tags);
  };

  // Build the final page config with all handlers and data
  const finalPageConfig = {
    ...pageLayoutConfig,
    rows: pageLayoutConfig.rows.map((row: any) => ({
      ...row,
      layoutConfig: {
        ...row.layoutConfig,
        elements: row.layoutConfig.elements.map((element: any) => ({
          ...element,
          props: {
            ...element.props,
            title: selectedGame
              ? `${config.title} - ${selectedGame.name}`
              : config.title,
            onCardClick: handleCardClick,
            onDelete: handleDelete,
            onAdd: handleAddWithFolder,
            onBulkAdd: itemType === "library" ? () => setIsBulkCreateWizardOpen(true) : undefined,
            renderCard: renderCard,
            cards: cards,
            groupedCards: groupedCards,
            selectedFolderId: selectedFolderId,
            filterConfig: filterConfigs,
            filterActions: {
              onAdd: handleAddWithFolder,
              onRefresh: refreshData,
            },
            onDeleteSelected: handleDeleteSelected,
            itemType: itemType,
            // Data view mode props
            availableSections: availableSections,
            availableFields: availableFields,
            rawItems: rawItems,
            templateLookup: templateLookup,
            folderPathLookup: folderPathLookup,
            // Cell value change handler for table editing
            onCellValueChange: handleCellValueChange,
            // Flow view props (only for supported types)
            ...(flowViewProps && {
              flowConfig: flowViewProps.flowConfig,
              selectedItemForFlow: flowViewProps.selectedItemForFlow,
              allowFlowView: flowViewProps.allowFlowView,
            }),
            // Pending changes props (only for supported types)
            ...(pendingChangesProps && {
              pendingChangesCount: pendingChangesProps.pendingChangesCount,
              hasPendingChanges: pendingChangesProps.hasPendingChanges,
              modifiedItems: pendingChangesProps.modifiedItems,
              modifiedCells: pendingChangesProps.modifiedCells,
              onSaveItem: pendingChangesProps.onSaveItem,
              onDiscardItem: pendingChangesProps.onDiscardItem,
              onSaveAllItems: pendingChangesProps.onSaveAllItems,
              onDiscardAllItems: pendingChangesProps.onDiscardAllItems,
              onForceSaveItem: pendingChangesProps.onForceSaveItem,
            }),
          },
        })),
      },
    })),
  };

  return (
    <PageLayout
      config={finalPageConfig}
      editMode={isEditMode}
      onConfigChange={handlePageConfigChange}
      onEditModeChange={setIsEditMode}
    />
  );
}
